import { useEffect, useMemo, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { Spinner } from '@trello/nachos/spinner';
import type { PIIString } from '@trello/privacy';

import {
  WorkspaceBoardsListItem,
  WorkspaceBoardsPrivateBoardSummaryItem,
} from './WorkspaceBoardsListItem';
import { useWorkspaceBoardsQuery } from './WorkspaceBoardsQuery.generated';

interface WorkspaceBoardsListProps {
  idMember: string;
  idOrganization: string;
  fullName?: PIIString | null;
  boardCount?: number;
  hidePopover: () => void;
}

export function WorkspaceBoardsList({
  idMember,
  idOrganization,
  fullName,
  boardCount,
  hidePopover,
}: WorkspaceBoardsListProps) {
  const [hasSentEvent, setHasSentEvent] = useState(false);

  // Queries for data needed to render every board item
  // Individual board items use fragments based on this query's data
  const { data: org, loading } = useWorkspaceBoardsQuery({
    variables: { idOrganization },
    waitOn: ['None'],
  });

  const boards = useMemo(
    () =>
      org?.organization?.boards?.filter((board) =>
        board.memberships.some(
          (membership) => membership.idMember === idMember,
        ),
      ) || [],
    [org?.organization?.boards, idMember],
  );

  const boardIds = useMemo(() => boards.map((board) => board.id), [boards]);

  // had a bug where sometimes it showed -1 private boards
  const privateBoardCount = Math.max(
    0,
    boardCount ? boardCount - boards.length : 0,
  );

  useEffect(() => {
    if (!loading && !hasSentEvent) {
      setHasSentEvent(true);
      Analytics.sendScreenEvent({
        name: 'memberActivityInlineDialog',
        containers: {
          organization: {
            id: idOrganization,
          },
        },
        attributes: {
          privateBoardCount,
          boardIds,
        },
      });
    }
  }, [
    boardIds,
    hasSentEvent,
    idOrganization,
    loading,
    privateBoardCount,
    setHasSentEvent,
  ]);

  const boardListItems = useMemo(
    () =>
      boards.map((board) => (
        <WorkspaceBoardsListItem
          key={board.id}
          idBoard={board.id}
          idMember={idMember}
          idOrganization={idOrganization}
        />
      )),
    [boards, idMember, idOrganization],
  );

  useEffect(() => {
    if (boardListItems.length === 0 && privateBoardCount === 0) {
      hidePopover();
    }
  }, [boardListItems.length, hidePopover, privateBoardCount]);

  return (
    <>
      <p>
        {intl.formatMessage(
          {
            id: 'templates.org_member_activity.name-is-member-of-boards',
            defaultMessage:
              '{name} is a member of the following Workspace boards:',
            description:
              "Heading above a list of a given user's boards in the current Workspace",
          },
          { name: fullName },
        )}
      </p>
      {loading ? (
        <Spinner centered />
      ) : (
        <ul>
          <WorkspaceBoardsPrivateBoardSummaryItem
            privateBoardCount={privateBoardCount}
          />
          {boardListItems}
        </ul>
      )}
    </>
  );
}
